// --- START OF FILE KickoffOwnerView.js ---
/* frontend/src/components/KickoffOwnerView.js */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { apiService } from "../services/api";
import ObjectiveItem from "./ObjectiveItem"; // Assurez-vous que ce chemin est correct

const KickoffOwnerView = ({
  projectId: propProjectId,
  variant = "standalone",
  refreshToken = 0,
  onKickoffSync,
  participants: participantsProp,
}) => {
  const projectId = Number(propProjectId);
  const isEmbedded = variant === "panel";
  const [objectives, setObjectives] = useState([]);
  const [inputObjective, setInputObjective] = useState(null);
  const [hoveredBlock, setHoveredBlock] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [sprints, setSprints] = useState([]);
  const [isLoadingParticipants, setIsLoadingParticipants] = useState(true);
  const [error, setError] = useState(null);

  // --- NOUVEAU: États pour gérer les suggestions de titre post-création ---
  const [suggestedTitles, setSuggestedTitles] = useState({});
  const [dismissedSuggestions, setDismissedSuggestions] = useState({});

  // Fetch employees
  useEffect(() => {
    setError(null);
    apiService.get("/employees")
      .then((data) => {
        setAllEmployees(Array.isArray(data) ? data : []);
      })
      .catch(err => { console.error("Error fetching employees:", err); setError("Erreur chargement employés."); });
  }, []);

  // Fetch objectives
  useEffect(() => {
    if (!projectId || isNaN(projectId)) return;
    setError(null);
    apiService.get(`/projects/${projectId}/objectives`)
      .then((data) => {
        const sortedObjectives = data.sort((a, b) => a.id - b.id).map(objective => ({ ...objective }));
        setObjectives(sortedObjectives);
      })
      .catch(err => { console.error("Error fetching objectives:", err); setError("Erreur chargement objectifs."); });
  }, [projectId, refreshToken]);

  // Fetch sprints
  useEffect(() => {
    if (!projectId || isNaN(projectId)) return;
    setError(null);
    apiService.get(`/projects/${projectId}/sprints`)
      .then(setSprints)
      .catch(err => { console.error("Error fetching sprints:", err); setError("Erreur chargement sprints."); });
  }, [projectId]);

  // Fetch participants (fallback when not provided via props)
  useEffect(() => {
      if (Array.isArray(participantsProp)) {
          setParticipants(participantsProp);
          setIsLoadingParticipants(false);
          return;
      }
      if (!projectId || isNaN(projectId) || allEmployees.length === 0) { setParticipants([]); setIsLoadingParticipants(false); return; }
      let isCancelled = false;
      setIsLoadingParticipants(true); setError(null); setParticipants([]);
      apiService.get(`/projects/${projectId}`)
        .then(projectData => {
          if (isCancelled) return [];
          const circleId = projectData?.circle_id;
          if (!circleId) return [];
          return apiService.get(`circle-focus?parent_id=${circleId}`).catch(() => []);
        })
        .then(focusEntries => {
          if (isCancelled) return;
          const token = `@[project:${projectId}|`;
          const byId = new Map();
          (Array.isArray(focusEntries) ? focusEntries : []).forEach(entry => {
              if (!entry?.focus_text || !entry.focus_text.includes(token)) return;
              const employee = allEmployees.find(emp => emp.id === entry.employee_id);
              if (employee) {
                  byId.set(employee.id, employee);
              }
          });
          setParticipants(Array.from(byId.values()));
          setIsLoadingParticipants(false);
        })
        .catch(err => {
          if (isCancelled) return;
          console.error("Error fetching focus participants:", err);
          setError("Erreur chargement participants.");
          setParticipants([]);
          setIsLoadingParticipants(false);
        });
      return () => { isCancelled = true; };
  }, [projectId, allEmployees, participantsProp]); // Rerun if projectId or employees change // Rerun if projectId or employees change

  // Determine display list
  const displayEmployees = useMemo(() => {
      if (isLoadingParticipants || error) return [];
      if (participants.length > 0) {
          return [...participants].sort((a, b) => a.name.localeCompare(b.name));
      }
      return [...allEmployees]
        .filter(emp => emp.is_active !== false)
        .sort((a, b) => a.name.localeCompare(b.name));
  }, [participants, allEmployees, isLoadingParticipants, error]);

  // --- Handlers pour accepter/refuser les suggestions ---
  const handleAcceptSuggestion = (objectiveId, newTitle) => {
    apiService.patch(`/projects/${projectId}/objectives/${objectiveId}`, { name: newTitle })
      .then(updatedObjective => {
        setObjectives(prev => prev.map(t => t.id === objectiveId ? { ...t, name: updatedObjective.name } : t));
        setSuggestedTitles(prev => {
          const newSuggestions = { ...prev };
          delete newSuggestions[objectiveId];
          return newSuggestions;
        });
        if (onKickoffSync) onKickoffSync("objectives");
      })
      .catch(err => {
        console.error("Erreur mise a jour du titre:", err);
        setError("Erreur mise a jour du titre.");
      });
  };

  const handleDeclineSuggestion = (objectiveId) => {
    setDismissedSuggestions(prev => ({ ...prev, [objectiveId]: true }));
  };

  const handleRequestSuggestion = async (objectiveId, objectiveName) => {
    try {
        const data = await apiService.suggestObjectiveTitle(projectId, objectiveName);
        if (data && data.suggested_title) {
            handleAcceptSuggestion(objectiveId, data.suggested_title);
        }
    } catch (error) {
        console.error("Erreur lors de la demande de suggestion de titre:", error);
        setError("Erreur de suggestion de titre IA.");
    }
  };

  // --- Drag & Drop Handlers ---
  const handleDragStart = (e, objectiveId) => e.dataTransfer.setData("text/plain", objectiveId);
  const handleDragOver = (e) => e.preventDefault();

  // Drop onto owner column
  const handleDropOnOwner = async (e, ownerId) => {
    e.preventDefault();
    const objectiveId = e.dataTransfer.getData("text/plain");
    const droppedObjective = objectives.find(objective => objective.id.toString() === objectiveId);
    if (!droppedObjective || String(droppedObjective.owner_id) === String(ownerId)) return;
    const objectiveProjectId = droppedObjective.project_id || projectId;

    setObjectives(prev => prev.map(objective => objective.id.toString() === objectiveId ? { ...objective, owner_id: ownerId, x: null, y: null } : objective));
    try {
      await apiService.patch(`/projects/${objectiveProjectId}/objectives/${objectiveId}`, { owner_id: ownerId });
      if (onKickoffSync) onKickoffSync("objectives");
    } catch (error) {
      console.error("Erreur affectation owner :", error); setError("Erreur affectation owner.");
      setObjectives(prev => prev.map(t => t.id.toString() === objectiveId ? droppedObjective : t)); // Revert
    }
  };

  // Drop onto bottom (unassigned) area
  const handleDropOnBottom = async (e) => {
    e.preventDefault(); e.stopPropagation();
    const objectiveId = e.dataTransfer.getData("text/plain");
    const droppedObjective = objectives.find(objective => objective.id.toString() === objectiveId);
    if (!droppedObjective || droppedObjective.owner_id === null) return;

    const objectiveProjectId = droppedObjective.project_id || projectId;

    setObjectives(prev => prev.map(objective => objective.id.toString() === objectiveId ? { ...objective, owner_id: null, x: null, y: null } : objective));
    try {
      await apiService.patch(`/projects/${objectiveProjectId}/objectives/${objectiveId}`, { owner_id: null });
      if (onKickoffSync) onKickoffSync("objectives");
    } catch (error) {
      console.error("Erreur désaffectation owner (backend):", error); setError("Erreur désaffectation owner.");
      setObjectives(prev => prev.map(t => t.id.toString() === objectiveId ? droppedObjective : t)); // Revert
    }
  };

  // --- Objective Creation ---
  const handleBottomClick = (e) => { if (e.target.closest('.objective-item-container')) return; setInputObjective({ value: "" }); };
  const handleInputChange = (e) => setInputObjective({ value: e.target.value });

  const handleInputBlur = () => {
    if (inputObjective && inputObjective.value.trim() !== "") {
      if (!projectId || isNaN(projectId)) { setError("Veuillez selectionner un projet valide."); setInputObjective(null); return; }
      const newObjectivePayload = { project_id: projectId, name: inputObjective.value.trim(), description: "", sprint_id: null, effort: 0, owner_id: null, status: "not_started", x: null, y: null };
      const tempId = -Date.now();
      const tempObjective = { id: tempId, ...newObjectivePayload, predicted_tokens: null };
      setObjectives(prev => [...prev, tempObjective].sort((a, b) => a.id - b.id));
      apiService.post(`/projects/${projectId}/objectives`, newObjectivePayload)
        .then(createdObjective => {
          setObjectives(prev => {
            const next = prev.map(t => t.id === tempId ? { ...t, ...createdObjective } : t);
            return next.slice().sort((a, b) => a.id - b.id);
          });
          if (onKickoffSync) onKickoffSync("objectives");
          apiService.suggestObjectiveTitle(projectId, createdObjective.name)
            .then(suggestionData => {
              if (suggestionData && suggestionData.suggested_title) {
                setSuggestedTitles(prev => ({...prev, [createdObjective.id]: suggestionData.suggested_title}));
              }
            }).catch(err => console.error("Erreur suggestion titre post-creation:", err));
        })
        .catch(error => {
          console.error("Erreur creation objectif :", error);
          setError("Erreur creation objectif.");
          setObjectives(prev => prev.filter(t => t.id !== tempId));
        });
    }
    setInputObjective(null);
  };
  const handleInputKeyDown = (e) => { if (e.key === "Enter") { e.preventDefault(); handleInputBlur(); } else if (e.key === "Escape") { e.preventDefault(); setInputObjective(null); } };

  // --- Objective Rendering ---
  const renderObjectiveCard = useCallback(
    (objective) => (
      <div key={objective.id} className="objective-item-container relative" draggable={true} onDragStart={(e) => handleDragStart(e, objective.id)}>
        <ObjectiveItem
          objective={objective}
          onDelete={(id) => {
            const objectiveProjectId = objective.project_id || projectId;
            apiService
              .delete(`/projects/${objectiveProjectId}/objectives/${id}`)
              .then(() => {
                setObjectives(prev => prev.filter(t => t.id !== id));
                if (onKickoffSync) onKickoffSync("objectives");
              })
              .catch(err => { console.error("Error deleting objective:", err); setError("Erreur suppression objectif."); });
          }}
          onUpdate={(updatedFields) => {
            const objectiveProjectId = objective.project_id || projectId;
            const { x, y, ...fieldsToSend } = updatedFields;
            if (Object.keys(fieldsToSend).length > 0) {
              apiService
                .patch(`/projects/${objectiveProjectId}/objectives/${objective.id}`, fieldsToSend)
                .then(response => {
                  setObjectives(prev => prev.map(t => t.id === objective.id ? { ...t, ...response } : t));
                  if (onKickoffSync) onKickoffSync("objectives");
                })
                .catch(err => { console.error("Erreur mise a jour :", err); setError("Erreur mise a jour objectif."); });
            }
          }}
          onDragOver={(e) => e.preventDefault()}
          sprintOptions={sprints} ownerOptions={allEmployees}
          showPredictedTokenUI={false}
          predictedTokens={objective.predicted_tokens}
          onPredictedTokenClick={(e) => { /* Pas d'action ici */ }}
          suggestedTitle={suggestedTitles[objective.id]}
          isSuggestionDismissed={dismissedSuggestions[objective.id]}
          onAcceptSuggestion={handleAcceptSuggestion}
          onDeclineSuggestion={handleDeclineSuggestion}
          onRequestSuggestion={handleRequestSuggestion}
        />
      </div>
    ),
    [projectId, sprints, allEmployees, objectives, suggestedTitles, dismissedSuggestions, onKickoffSync]
  );

  // Group objectives by owner ID - SORTED
  const objectivesByOwner = useMemo(() => { const grouped = {}; displayEmployees.forEach(emp => { grouped[emp.id] = objectives.filter(objective => String(objective.owner_id) === String(emp.id) && objective.status !== "done").sort((a, b) => a.id - b.id); }); return grouped; }, [displayEmployees, objectives]);
  const objectivesUnassigned = objectives.filter(objective => !objective.owner_id && objective.status !== "done").sort((a, b) => a.id - b.id);
  const isShowingDefaultAll = !isLoadingParticipants && !error && participants.length === 0 && allEmployees.length > 0;

  const containerClass = isEmbedded ? "flex flex-col gap-4" : "h-screen flex flex-col";
  const boardClass = isEmbedded
    ? "rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-sm overflow-x-auto overflow-y-hidden flex space-x-4 no-scrollbar relative"
    : "h-[45%] bg-white p-4 border-b overflow-x-auto overflow-y-hidden flex space-x-4 no-scrollbar relative";
  const backlogClass = isEmbedded
    ? "relative rounded-2xl border border-dashed border-slate-300 bg-slate-50/80 p-4 overflow-y-auto no-scrollbar"
    : "h-[35%] relative bg-gray-50 p-4 border border-dashed border-gray-300 rounded overflow-y-auto no-scrollbar";

  return (
    <div className={containerClass}>
      {error && (<div className="mb-2 rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700"><button onClick={() => setError(null)} className="float-right font-bold text-red-900">×</button>{error}</div>)}
      <div className={boardClass} style={{ msOverflowStyle: 'none', scrollbarWidth: 'none' }}>
        {isLoadingParticipants ? (<div className="flex items-center justify-center w-full text-gray-500">Chargement des participants...</div>)
         : displayEmployees.length === 0 ? (<div className="flex items-center justify-center w-full text-gray-500 italic">Aucun employé {participants.length > 0 ? 'participant' : 'actif'} trouvé.</div>)
         : (<>
             {isShowingDefaultAll && (<div className="absolute top-1 right-4 text-xs italic text-gray-400 p-1 bg-white rounded shadow-sm z-10">Tous les employés actifs (aucun participant spécifique défini)</div>)}
              {displayEmployees.map(employee => (
                   <div key={employee.id} className="flex-1 min-w-[220px] flex flex-col p-3 rounded-2xl border border-slate-100 bg-white/90 shadow-sm h-full"
                      onDragOver={handleDragOver} onDrop={(e) => handleDropOnOwner(e, employee.id)}
                      onDragEnter={(e) => { e.preventDefault(); setHoveredBlock(employee.id); }}
                      onDragLeave={(e) => { e.preventDefault(); setHoveredBlock(null); }}
                      style={{ backgroundColor: hoveredBlock === employee.id ? '#f0f9ff' : 'white' }}>
                    <div className="text-center font-bold mb-2 text-sm flex items-center justify-center space-x-2 sticky top-0 bg-white py-1 z-10 flex-shrink-0">
                       {employee.picture ? (<img src={employee.picture} alt={employee.name} className="w-6 h-6 rounded-full object-cover"/>) : (<span className="flex items-center justify-center w-6 h-6 rounded-full bg-gray-300 text-gray-600 text-[10px] font-bold">{employee.name ? employee.name.split(' ').map(n=>n[0]).join('').toUpperCase() : '?'}</span>)}
                       <span>{employee.name}</span>
                    </div>
                    <div className="flex-1 overflow-y-auto no-scrollbar mt-1 space-y-1 -mr-1 pr-1" style={{ msOverflowStyle: 'none', scrollbarWidth: 'none' }}>
                      {objectivesByOwner[employee.id]?.length > 0 ? (
                        objectivesByOwner[employee.id].map(objective => renderObjectiveCard(objective))
                      ) : (<p className="text-center text-xs text-gray-500 pt-4">Glissez un objectif ici</p>)}
                    </div>
                  </div> ))}
             </>)}
      </div>
      <div className={backlogClass}
           onClick={handleBottomClick} onDrop={handleDropOnBottom} onDragOver={handleDragOver}
           style={{ msOverflowStyle: 'none', scrollbarWidth: 'none' }}>
          <div className="space-y-1">
            {objectivesUnassigned.map(objective => renderObjectiveCard(objective))}
          </div>
          {inputObjective && (
            <div className="mt-2">
                <input autoFocus type="text" value={inputObjective.value} onChange={handleInputChange} onBlur={handleInputBlur} onKeyDown={handleInputKeyDown} className="border rounded p-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 w-full" placeholder="Nom du nouvel objectif"/>
            </div>
          )}
          {!inputObjective && objectivesUnassigned.length === 0 && (
             <div className="absolute inset-0 flex items-center justify-center text-sm text-slate-400 pointer-events-none">
                 Glissez ici pour désassigner ou cliquez pour ajouter
            </div>
          )}
      </div>
    </div>
  );
};
export default KickoffOwnerView;
// --- END OF FILE KickoffOwnerView.js ---
